import { createRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import {
  portalHostLabel,
  resolvePortalTenantSlug,
  tenantScopedPath,
} from "../lib/portal-tenant-path.js";

import { rootRoute } from "./__root.js";

/**
 * Apex `/` — send browsers into `/t/<slug>/…`.
 *
 *   - Leaf host `testra.portal…` → `/t/testra/`
 *   - Family `gop.portal…` → `/t/gop/` (dual-mode)
 *   - Family `devices.portal…` without a slug → short message
 *   - Localhost → `/t/<DEFAULT or gop>/` via resolve helper when set
 */
export const rootEntryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: RootEntry,
});

function RootEntry() {
  const navigate = useNavigate();
  const slug = resolvePortalTenantSlug();
  const hostLabel = typeof window !== "undefined" ? portalHostLabel(window.location.host) : null;

  useEffect(() => {
    if (slug) {
      void navigate({ href: tenantScopedPath(slug, "/"), replace: true });
    }
  }, [navigate, slug]);

  if (slug) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-muted text-sm text-fg-muted">
        Opening portal…
      </div>
    );
  }

  if (hostLabel === "devices") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-muted px-6">
        <div className="max-w-md space-y-3 text-center">
          <h1 className="text-xl font-semibold text-fg">Devices portal</h1>
          <p className="text-sm text-fg-muted">
            Open the link your Latitude30 contact sent you. It looks like{" "}
            <code className="rounded bg-surface px-1.5 py-0.5 text-fg">
              /t/&lt;partner&gt;/portal
            </code>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-muted px-6">
      <div className="max-w-md space-y-3 text-center">
        <h1 className="text-xl font-semibold text-fg">Portal</h1>
        <p className="text-sm text-fg-muted">
          Use your partner portal URL (for example{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 text-fg">/t/gop/portal/sign-in</code>
          ).
        </p>
      </div>
    </div>
  );
}

/**
 * Legacy flat paths (`/portal/…` without `/t/<slug>`) — rewrite into
 * the tenant-scoped tree using host or dual-mode `gop`.
 */
export const legacyPortalRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/portal/$",
  component: LegacyPortalRedirect,
});

function LegacyPortalRedirect() {
  const navigate = useNavigate();
  const slug = resolvePortalTenantSlug();
  const rest =
    typeof window !== "undefined" ? window.location.pathname.replace(/^\/portal/, "") || "" : "";
  const search = typeof window !== "undefined" ? window.location.search : "";

  useEffect(() => {
    if (!slug) return;
    const target = `${tenantScopedPath(slug, `/portal${rest}`)}${search}`;
    void navigate({ href: target, replace: true });
  }, [navigate, rest, search, slug]);

  if (!slug) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-muted px-6 text-sm text-fg-muted">
        Missing portal tenant in the URL. Open your `/t/&lt;partner&gt;/portal` link.
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-muted text-sm text-fg-muted">
      Redirecting…
    </div>
  );
}
